import { FeatureGuard } from "../tenant/features/FeatureGuard";
import PermissionGuard from "../guards/PermissionGuard";

/**
 * Wrap a route element in the access guards it declares:
 *  - `feature`    -> FeatureGuard  (is the capability enabled for this tenant?)
 *  - `permission` -> PermissionGuard (does this user's role allow it?)
 *
 * A page is reachable only if BOTH conditions hold. FeatureGuard is the outer guard
 * (a disabled feature hides the page for the whole tenant regardless of permission);
 * PermissionGuard is inner. Either field may be omitted — then that condition is skipped,
 * so a route can be permission-only, feature-only, or unguarded. Removing the `feature`
 * field once a feature becomes standard leaves a plain permission check, nothing else.
 */
function wrapElement(element: React.ReactNode, feature?: string, permission?: number) {
  let wrapped = element;

  if (permission) {
    wrapped = <PermissionGuard permission={permission}>{wrapped}</PermissionGuard>;
  }

  if (feature) {
    wrapped = <FeatureGuard feature={feature}>{wrapped}</FeatureGuard>;
  }

  return wrapped;
}

export function withAccess(routes: any) {
  return routes.map((r: any) => ({
    ...r,
    element: wrapElement(r.element, r.feature, r.permission),
    children: r.children ? withAccess(r.children) : undefined,
  }));
}
